/*
  Journey Editor Async Thunks

  This module contains all async operations for the journey editor,
  including API transformations, outcome enrichment, and workflow caching.
*/

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  forwardTransform,
  assignConditionPublicIds,
  enrichOutcomeNodes,
  assignConditionIdsFromOutcomes,
  reverseTransform,
  enrichWorkflowEdgesWithPrecedence,
  extractOutcomesFromCachedWorkflow,
} from '../engine';
import type {
  ApiJourneyVersionData,
  JourneyDocument,
  SaveJourneyVersionPayload,
  JourneyWorkflowMetadata,
} from '../types';
import type { InvestigationType } from '../types/shared';
import type { Outcome } from '../../settings/outcomes/types';
import apiCalls from '~/helpers/apiCalls';
import { getMatrixModels } from '~/flow/matrix-models/services';
import { selectJourneyWorkflowsFromPartial } from './selectors';
import {
  setLoading,
  setError,
  setDocumentAndSnapshot,
  updateDocument,
  setJourneyWorkflows,
  setProps,
} from './slice';
import type { StateWithWorkflows, RootStateWithJourneyEditor } from './types';
import type { JourneyEditorProps } from './state';
import {
  buildWorkflowProps,
  initializeWorkflowEditor,
} from './workflowHelpers';
import { getAllowedJourneyOutcomes } from './helpers';
import { ajaxFetch } from '~/helpers/api';
import { routes } from '~/helpers/routes';

const { journeyActions: { getJourneyActions } } = apiCalls;
const WORKFLOW_QUERY_PARAMS = 'paginateBy=1000';

// ============================================================================
// Document Transformation Thunks
// ============================================================================

/**
 * Transforms API data to document model and stores both.
 *
 * This is the primary hydration method that converts backend API format
 * to the internal document model used by the journey editor.
 */
export const shadowHydrateFromApi = createAsyncThunk(
  'journeyEditor/shadowHydrateFromApi',
  async (
    params: {
      apiData: ApiJourneyVersionData;
      investigationTypes?: InvestigationType[];
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading());
      const { apiData, investigationTypes } = params;
      const doc = forwardTransform(apiData, { investigationTypes });
      const enrichedDoc = assignConditionPublicIds(doc);
      dispatch(setDocumentAndSnapshot({ doc: enrichedDoc, apiData }));
      return { doc: enrichedDoc, apiData };
    } catch (error: any) {
      dispatch(setError(error?.message || 'Failed to hydrate from API'));
      throw error;
    }
  },
);

/**
 * Prepares document model for saving to backend.
 *
 * Converts internal document model back to backend API format
 * with all required metadata for saving.
 */
export const shadowPrepareSaveToApi = createAsyncThunk(
  'journeyEditor/shadowPrepareSaveToApi',
  async (
    params: {
      doc: JourneyDocument;
      options?: {
        active?: boolean;
        changeLog?: string;
        isMultiEntity?: boolean;
        journeyNotes?: string;
        parentVersion?: number;
        versionId?: number;
      };
    },
    { dispatch },
  ): Promise<SaveJourneyVersionPayload> => {
    try {
      dispatch(setLoading());
      const { doc, options = {} } = params;
      const result = reverseTransform(doc, options);
      // No need to update state - just return the payload
      return result;
    } catch (error: any) {
      dispatch(setError(error?.message || 'Failed to prepare save payload'));
      throw error;
    }
  },
);

/**
 * Enriches outcome nodes with data from the general outcomes endpoint.
 *
 * Fetches available outcomes for the journey type and enriches the document
 * nodes with display metadata (colors, icons, etc).
 */
export const enrichOutcomesInDoc = createAsyncThunk(
  'journeyEditor/enrichOutcomesInDoc',
  async (
    {
      doc,
      journeyType,
      typhonDomain,
    }: {
      doc?: JourneyDocument;
      journeyType: string;
      typhonDomain: string;
    },
    { getState, dispatch },
  ) => {
    try {
      dispatch(setLoading());
      const state = getState() as RootStateWithJourneyEditor;
      const baseDoc: JourneyDocument | null = doc || state.journeyEditor.doc;

      if (!baseDoc) {
        dispatch(setError('No document to enrich'));
        throw new Error('No document to enrich');
      }

      // Fetch allowed journey outcomes
      const lists = await getAllowedJourneyOutcomes({
        journeyType: journeyType as any,
        typhonDomain,
      });

      // Convert to backend outcome format
      const backendOutcomes: Outcome[] = lists.journeyOutcomes.map(o => ({
        ...o,
        outcome: o.outcome,
        customer_outcome_id: o.customer_outcome_id,
        icon_name: o.icon_name,
        outcome_color: o.outcome_color,
      }));

      // Enrich the document
      const enrichedDoc = enrichOutcomeNodes(baseDoc, backendOutcomes);
      dispatch(updateDocument(enrichedDoc));
      return enrichedDoc;
    } catch (error: any) {
      dispatch(setError(error?.message || 'Failed to enrich outcomes'));
      throw error;
    }
  },
);

/**
 * Enriches edges from workflow nodes with precedence data.
 *
 * This ensures that edges from workflow nodes are ordered according to the
 * workflow's outcome action precedence, even for existing journeys that were
 * created before precedence tracking was implemented.
 *
 * Uses outcomes from workflows metadata that were extracted when workflows were cached.
 */
export const enrichWorkflowEdgesInDoc = createAsyncThunk(
  'journeyEditor/enrichWorkflowEdgesInDoc',
  async (
    {
      doc,
    }: {
      doc?: JourneyDocument;
    },
    { getState, dispatch },
  ) => {
    try {
      dispatch(setLoading());
      const state = getState() as RootStateWithJourneyEditor;
      const baseDoc: JourneyDocument | null = doc || state.journeyEditor.doc;

      if (!baseDoc) {
        dispatch(setError('No document to enrich'));
        throw new Error('No document to enrich');
      }

      // Get workflows metadata with extracted outcomes
      const workflows = selectJourneyWorkflowsFromPartial(state);

      // Enrich edges with precedence from workflow outcome actions
      const enrichedDoc = enrichWorkflowEdgesWithPrecedence(baseDoc, workflows);

      dispatch(updateDocument(enrichedDoc));
      return enrichedDoc;
    } catch (error: any) {
      dispatch(setError(error?.message || 'Failed to enrich workflow edges'));
      throw error;
    }
  },
);

/**
 * Fetches all reference data needed for the journey editor.
 *
 * This includes workflows, journey actions, and outcomes with their customer_outcome_ids.
 * This data is used by the AddNodeForm to build the node menu and to properly set
 * condition_id on edges when creating new nodes with outcomes.
 */
export const fetchJourneyProps = createAsyncThunk(
  'journeyEditor/fetchJourneyProps',
  async (
    {
      journeyType,
      typhonDomain,
    }: {
      journeyType: 'application' | 'alert';
      typhonDomain: string;
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading());

      // Fetch workflows
      const workflowRoute = `${routes.applications.listData}?${WORKFLOW_QUERY_PARAMS}`;
      const workflowData = await ajaxFetch(workflowRoute, 'GET');
      const workflows = workflowData?.data?.[0]?.data || [];

      // Fetch journey actions (outcomes come as string[])
      const actions = await getJourneyActions({
        typhonDomain,
        activeOnly: true,
        includeArchived: true,
      }) || [];

      // Fetch all outcomes for the journey type
      // This is used to build the outcome map for condition_id lookup
      const outcomeLists = await getAllowedJourneyOutcomes({
        journeyType,
        typhonDomain,
      });

      // Combine all outcome types that might be used in edges
      const allOutcomes = [
        ...outcomeLists.journeyOutcomes,
        ...outcomeLists.workflowAndActionOutcomes,
      ];

      const props: JourneyEditorProps = {
        workflows,
        actions, // Actions have outcomes as string[] - customer_outcome_id looked up from allOutcomes
        outcomes: allOutcomes,
      };

      dispatch(setProps(props));
      return props;
    } catch (error: any) {
      dispatch(setError(error?.message || 'Failed to fetch journey props'));
      throw error;
    }
  },
);

/**
 * Assigns condition_id to all edges in the document based on the loaded outcomes.
 *
 * This should be called after fetchJourneyProps completes to enrich existing edges
 * that were loaded from the API (forward transformation). This ensures all edges
 * have the proper condition_id for workflow/action outcomes, not just outcome nodes.
 */
export const enrichEdgesWithConditionIds = createAsyncThunk(
  'journeyEditor/enrichEdgesWithConditionIds',
  async (_, { getState, dispatch }) => {
    try {
      const state = getState() as RootStateWithJourneyEditor;
      const { doc, props } = state.journeyEditor;

      if (!doc) {
        throw new Error('No document to enrich');
      }

      if (!props?.outcomes) {
        throw new Error('No outcomes available');
      }

      // Build outcome map from props
      const outcomeMap: Record<string, number | null> = {};
      props.outcomes.forEach(outcome => {
        outcomeMap[outcome.outcome] = outcome.customer_outcome_id || null;
      });

      // Assign condition_id to all edges based on outcome map
      const enrichedDoc = assignConditionIdsFromOutcomes(doc, outcomeMap);

      dispatch(updateDocument(enrichedDoc));
      return enrichedDoc;
    } catch (error: any) {
      dispatch(setError(error?.message || 'Failed to enrich edges with condition IDs'));
      throw error;
    }
  },
);

// ============================================================================
// Workflow Caching and Initialization Thunks
// ============================================================================

/**
 * Fetches workflow data and stores it in journey editor Redux.
 *
 * This thunk caches workflow data for embedded display but does NOT
 * initialize the workflow editor. Use `initializeEmbeddedWorkflow` for that.
 *
 * Skips fetching if workflow is already cached or currently being cached.
 */
export const fetchAndCacheWorkflow = createAsyncThunk(
  'journeyEditor/fetchAndCacheWorkflow',
  async (
    {
      workflowToken,
      typhonDomain,
    }: {
      workflowToken: string;
      typhonDomain: string;
    },
    { getState, dispatch },
  ) => {
    const state = getState() as StateWithWorkflows;
    const workflows = selectJourneyWorkflowsFromPartial(state);
    const workflowMeta = workflows[workflowToken];

    // Skip if already cached or currently caching
    if (!workflowMeta || workflowMeta.initialized || workflowMeta.isInitializing) {
      return workflowMeta;
    }

    // Mark as initializing
    dispatch(setJourneyWorkflows({
      ...workflows,
      [workflowToken]: { ...workflowMeta, isInitializing: true, error: null },
    }));

    try {
      const versionId = workflowMeta.workflow_version;

      // Fetch workflow data and props in parallel for better performance
      const [workflowData, baseWorkflowProps] = await Promise.all([
        apiCalls.workflows.getWorkflowVersion({
          applicationToken: workflowToken,
          publicVersion: versionId || undefined,
        }),
        apiCalls.workflows.getWorkflowProps({ workflowToken }),
      ]);

      // Fetch matrix models if workflow has any matrix model actions
      const matrixModels = await getMatrixModels(typhonDomain, workflowData);

      // Build workflow props for workflow editor
      const workflowProps = buildWorkflowProps(
        baseWorkflowProps,
        workflowData,
        matrixModels,
      );

      // Extract outcomes from workflow data for edge enrichment and terminal recon synthetics
      const outcomes = extractOutcomesFromCachedWorkflow(workflowData);

      // Get fresh state to avoid overwriting changes from parallel fetches
      const freshState = getState() as StateWithWorkflows;
      const freshWorkflows = selectJourneyWorkflowsFromPartial(freshState);

      // Store cached data in journey Redux
      const updatedMeta: JourneyWorkflowMetadata = {
        ...freshWorkflows[workflowToken], // Use fresh metadata as base
        workflow_version: workflowData?.publicVersion ?? workflowMeta.workflow_version ?? null,
        workflow_name: workflowProps.workflow?.name ?? workflowMeta.workflow_name ?? null,
        workflow_type: workflowProps.workflow?.type ?? workflowMeta.workflow_type ?? null,
        initialized: true,
        isInitializing: false,
        error: null,
        cachedWorkflowData: null, // Not caching full workflow data for now
        cachedWorkflowProps: null, // Not caching workflow props for now
        outcomes, // Store extracted outcomes for edge enrichment and terminal recon synthetics
      };

      dispatch(setJourneyWorkflows({
        ...freshWorkflows,
        [workflowToken]: updatedMeta,
      }));

      return updatedMeta;
    } catch (error: any) {
      // Get fresh state to avoid overwriting changes from parallel fetches
      const freshState = getState() as StateWithWorkflows;
      const freshWorkflows = selectJourneyWorkflowsFromPartial(freshState);

      // Update state with error
      dispatch(setJourneyWorkflows({
        ...freshWorkflows,
        [workflowToken]: {
          ...freshWorkflows[workflowToken],
          initialized: false,
          isInitializing: false,
          error: error?.message || 'Failed to fetch workflow',
        },
      }));

      throw error;
    }
  },
);

/**
 * Initializes workflow editor Redux using cached data from journey Redux.
 *
 * If the workflow isn't cached yet, fetches and caches it first.
 * Then initializes the workflow editor state for embedded display.
 */
export const initializeEmbeddedWorkflow = createAsyncThunk(
  'journeyEditor/initializeEmbeddedWorkflow',
  async (
    {
      workflowToken,
      typhonDomain,
    }: {
      workflowToken: string;
      typhonDomain: string;
    },
    { getState, dispatch },
  ) => {
    const state = getState() as StateWithWorkflows;
    const workflows = selectJourneyWorkflowsFromPartial(state);
    const workflowMeta = workflows[workflowToken];

    if (!workflowMeta) {
      throw new Error(`Workflow ${workflowToken} not found in journey metadata`);
    }

    // If not yet cached, fetch and cache it first
    if (!workflowMeta.initialized && !workflowMeta.isInitializing) {
      await dispatch(fetchAndCacheWorkflow({ workflowToken, typhonDomain })).unwrap();

      // Get updated metadata after caching
      const updatedState = getState() as StateWithWorkflows;
      const updatedWorkflows = selectJourneyWorkflowsFromPartial(updatedState);
      const cachedMeta = updatedWorkflows[workflowToken];

      // Initialize workflow editor if we have cached data
      if (cachedMeta?.cachedWorkflowData && cachedMeta?.cachedWorkflowProps) {
        initializeWorkflowEditor(
          dispatch,
          workflowToken,
          cachedMeta.cachedWorkflowData,
          cachedMeta.cachedWorkflowProps,
        );
      }

      return cachedMeta;
    }

    // Already cached, just initialize workflow editor
    if (workflowMeta.cachedWorkflowData && workflowMeta.cachedWorkflowProps) {
      initializeWorkflowEditor(
        dispatch,
        workflowToken,
        workflowMeta.cachedWorkflowData,
        workflowMeta.cachedWorkflowProps,
      );
    }

    return workflowMeta;
  },
);

/**
 * Preload all workflows in the journey to reduce lag when expanding them.
 *
 * Fetches and caches workflow data for all workflows referenced in the journey
 * without initializing any workflow editors. This is useful for improving
 * perceived performance by front-loading API calls.
 */
export const preloadAllWorkflows = createAsyncThunk(
  'journeyEditor/preloadAllWorkflows',
  async (
    {
      typhonDomain,
    }: {
      typhonDomain: string;
    },
    { getState, dispatch },
  ) => {
    const state = getState() as StateWithWorkflows;
    const workflows = selectJourneyWorkflowsFromPartial(state);
    const workflowTokens = Object.keys(workflows);

    if (workflowTokens.length === 0) {
      return { preloaded: 0, failed: 0 };
    }

    // Fetch and cache all workflows in parallel
    const results = await Promise.allSettled(
      workflowTokens.map(
        workflowToken => dispatch(fetchAndCacheWorkflow({ workflowToken, typhonDomain })).unwrap(),
      ),
    );

    const preloaded = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;

    return { preloaded, failed };
  },
);
